import { CheckCircleFilled } from '@ant-design/icons';
import {
  Box,
  Button,
  Center,
  Container,
  Heading,
  Text,
} from '@chakra-ui/react';
import InputTextGroup from 'components/inputs/InputTextGroup';
import PageHeader from 'components/page-header/PageHeader';
import PageTitleMobile from 'components/page-header/PageTitleMobile';
import useCatchError from 'hooks/useCatchError';
import { withIronSessionSsr } from 'iron-session/next';
import RootLayout from 'layouts/RootLayout';
import { sessionOptions } from 'libs/iron-session';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Head from 'next/head';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { AuthRepository } from 'respository/auth.repository';
import { NextPageWithLayout } from './_app';

const ForgotPasswordPage: NextPageWithLayout = () => {
  // use react-hook-form
  const { control, handleSubmit } = useForm();

  // use catch error
  const catchErrors = useCatchError();

  const { t } = useTranslation(['auth', 'common']);

  // loading state
  const [loading, setLoading] = useState(false);

  // is reset password success
  const [isResetPasswordSuccess, setIsResetPasswordSuccess] = useState(false);

  const [curentEmail, setCurentEmail] = useState('');

  // handle submit
  const submit = async (values: any) => {
    console.log(values);
    try {
      setLoading(true);

      // fake api call promise
      const data = await AuthRepository.sendResetPassword({
        email: values.email,
      });

      if (data && data.success) {
        setIsResetPasswordSuccess(true);
        setCurentEmail(values.email);
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      catchErrors(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Head>
        <title>
          {t('auth.forgotPassword.title', {
            // TH: ลืมรหัสผ่าน
            // EN: Forgot Password
          })}
        </title>
      </Head>
      <Box>
        <PageHeader title={t('auth.forgotPassword.title')}></PageHeader>
        <PageTitleMobile>{t('auth.forgotPassword.title')}</PageTitleMobile>
        <Container>
          <Box
            pt={{
              base: 4,
              md: 8,
            }}
            pb={{
              base: 10,
              md: 12,
              lg: 24,
            }}
          >
            <Heading
              mb={{
                base: 2,
                md: 4,
              }}
              fontSize={{
                base: 'xl',
                lg: '2xl',
              }}
              fontWeight={400}
            >
              {t('auth.forgotPassword.title')}?
            </Heading>
            {isResetPasswordSuccess ? (
              <Box>
                <Box fontSize="5xl" color="green" mb={1}>
                  <CheckCircleFilled></CheckCircleFilled>
                </Box>
                <Box>
                  <Heading mb={1}>
                    {t('auth.forgotPassword.success.title', {
                      // TH: เราได้ส่งลิงก์เพื่อรีเซ็ตรหัสผ่านของคุณแล้วที่
                      // EN: We have sent a link to reset your password to
                    })}
                  </Heading>
                  <Text fontSize="lg">{curentEmail}</Text>
                </Box>
              </Box>
            ) : (
              <>
                <Text fontSize="lg" mb={4}>
                  {t('auth.forgotPassword.description')}
                </Text>
                <form onSubmit={handleSubmit(submit)}>
                  <Box width="250px">
                    <InputTextGroup
                      name="email"
                      type="email"
                      label={t('form.label.email', { ns: 'common' })}
                      control={control}
                      rules={{
                        required: {
                          value: true,
                          message: t('form.message.required', { ns: 'common' }),
                        },
                      }}
                    ></InputTextGroup>
                    <Box mt={4}>
                      <Button
                        type="submit"
                        width="100%"
                        bg="gray.800"
                        isLoading={loading}
                      >
                        {t('ok', { ns: 'common' })}
                      </Button>
                    </Box>
                  </Box>
                </form>
              </>
            )}
          </Box>
        </Container>
      </Box>
    </>
  );
};

ForgotPasswordPage.getLayout = (page) => {
  return <RootLayout>{page}</RootLayout>;
};

export const getServerSideProps = withIronSessionSsr(async function ({
  req,
  res,
  locale,
}) {
  const user = req.session.user;

  const _locale = locale || 'th';

  // log user
  console.log('user', user);

  if (user && user?.accessToken) {
    return {
      redirect: {
        destination: '/',
        permanent: false,
      },
    };
  }

  return {
    props: {
      ...(await serverSideTranslations(_locale, ['common', 'auth'])),
    },
  };
},
sessionOptions);

export default ForgotPasswordPage;
